import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import classNames from 'classnames';

import Button from '../global/Button';
import Alert from '../global/Alert';
import { ALBUM_SUBTYPE, ALBUM_TYPE } from '../../constants/albums';
import DiscoverAlbumFolderSummaryIdentity from './DiscoverAlbumFolderSummaryIdentity';
import {
  allowsJoinTypeSelection,
  getAlbumFolderDiscoveryOnboardingItems,
  getAllSelectedAlbumCustomPrints,
  getSelectedIdentitiesNameString,
  getTotalCheckoutPrice,
  selectedAlbumCustomPrintWillShip,
  shouldDisplayBrandAttribution,
} from '../../util/discover-album-folder';
import { CURRENCY } from '../../constants/currency';
import CheckoutPrice from '../purchase/CheckoutPrice';
import getConfig from '../../util/config';
import { sentryCaptureMessage } from '../../util/sentry';
import {
  albumFolderHasCharity,
  albumFolderIsPrePaid,
  albumFolderRequiresPhotoSearchOnboarding,
  isPrepaidOnlyAlbumFolder,
} from '../../util/album-folder';
import ContextualString from '../contextual/ContextualString';
import { getAccountCheckoutGreetingName } from '../../util/account';
import { canSelectAlbumCustomPrintShipping } from '../../util/custom-prints';
import AlbumCustomPrintShippingSelection from './AlbumCustomPrintShippingSelection';
import PhotographerAttributionBanner from './PhotographerAttributionBanner';
import DiscoverAlbumFolderUserAddressContainer from '../../containers/discover/DiscoverAlbumFolderUserAddressContainer';
import { gtm } from '../../util/analytics';
import { STRIPE_CHECKOUT_MODAL } from '../global/StripeCheckout';
import { formatPriceForDisplay } from '../../util/format-money';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import { ALBUM_IDENTITY_ACCESS } from '../../constants/identity';
import OnboardingLayoutFooter from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';
import { isEligibleForShutterbugFreeTrial } from '../../apps/temp/views/onboarding/PhotoSearchCheckout/helpers';
import Badge from '../global/Badge';
import PriceDisplay from '../global/PriceDisplay';
import { DISCOUNT_MODE } from '../../util/promo-codes';
import { ACCOUNT_REFERRAL_TYPE } from '../../constants/account-referrals';
import { SHUTTERBUG_TRIAL_DAYS_FALLBACK } from '../../apps/temp/views/onboarding/PhotoSearchUpsell/helpers';
import PlusIcon from '../icons/PlusIcon';
import SiblingDiscountAlertModal, {
  SIBLING_DISCOUNT_ALERT_MODAL,
} from './SiblingDiscountAlert';
import { closeModal } from '../../actions/modal-actions';

class DiscoverAlbumFolderSummary extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        albumFolderId: PropTypes.string.isRequired,
      }),
    }),
    user: PropTypes.shape({
      id: PropTypes.string,
    }),
    userAddress: PropTypes.shape({
      id: PropTypes.string,
    }),
    selectedIdentities: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        selectedAlbums: PropTypes.arrayOf(PropTypes.object).isRequired,
        relationship: PropTypes.string,
      }),
    ).isRequired,
    onDeselectAlbum: PropTypes.func.isRequired,
    onDeselectAlbumCustomPrint: PropTypes.func.isRequired,
    onSelectAlbumCustomPrint: PropTypes.func.isRequired,
    checkoutAlbums: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    success: PropTypes.bool,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      is_demo: PropTypes.bool,
      album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
      album_subtype: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
      has_charity: PropTypes.bool,
      charity_name: PropTypes.string,
      charity_percentage: PropTypes.number,
    }),
    checkoutPrice: PropTypes.shape({
      total_price: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
      total_shipping_cost: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
      total_tax: PropTypes.shape({
        value: PropTypes.number,
        currency: PropTypes.oneOf(Object.values(CURRENCY)),
      }),
    }),
    loadingCheckoutPrice: PropTypes.bool,
    loadingUserAddress: PropTypes.bool,
    refetchCheckoutPrice: PropTypes.func,
    errorLoadingPrice: PropTypes.object,
    onSelectIdentity: PropTypes.func.isRequired,
    onDeselectIdentity: PropTypes.func.isRequired,
    setJoinTypeSelectionFlag: PropTypes.func.isRequired,
    openModal: PropTypes.func.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  };

  state = {
    expanded: [],
    skipPrintPackages: false,
  };

  componentDidMount() {
    this.checkForSiblingDiscount();
  }

  componentDidUpdate(prevProps) {
    const {
      success,
      history,
      albumFolder,
      loadingCheckoutPrice,
      loadingUserAddress,
      checkoutPrice,
      errorLoadingPrice,
      user,
      refetchCheckoutPrice,
      allowsPrepaidItems,
      clearCart,
      selectedIdentities,
    } = this.props;

    const { skipPrintPackages } = this.state;

    if (!albumFolder) {
      return;
    }

    // If the gallery allows for Prepaid Print Packages,
    // direct the user to the Print Package Purchase page.
    if (
      success &&
      !prevProps.success &&
      allowsPrepaidItems &&
      !isPrepaidOnlyAlbumFolder(albumFolder) &&
      !skipPrintPackages
    ) {
      history.replace(
        `/discover/album-folder/${albumFolder.id}/pre-purchase?skippable=1`,
      );
      return;
    }

    if (success && !prevProps.success) {
      // Clear cart as purchase has happened.
      if (isPrepaidOnlyAlbumFolder(albumFolder)) {
        clearCart?.();
      }
      history.replace(`/discover/album-folder/${albumFolder.id}/success`);
      return;
    }

    if (errorLoadingPrice) {
      sentryCaptureMessage(errorLoadingPrice, {
        level: 'error',
        extra: {
          accountId: user?.id,
          albumFolderId: albumFolder.id,
        },
      });
    }

    if (
      !loadingCheckoutPrice &&
      checkoutPrice == null &&
      !errorLoadingPrice &&
      !loadingUserAddress &&
      user
    ) {
      if (refetchCheckoutPrice) {
        // We might skip the query at some point.
        // If we do, this value is undefined.
        refetchCheckoutPrice();
      }

      sentryCaptureMessage('Final price failed to load', {
        level: 'error',
        extra: {
          accountId: user?.id,
          albumFolderId: albumFolder.id,
          checkoutPrice,
        },
      });
    }

    if (prevProps.selectedIdentities?.length !== selectedIdentities?.length) {
      this.checkForSiblingDiscount();
    }
  }

  onEditIdentitySelections = identity => {
    const {
      onSelectIdentity,
      setJoinTypeSelectionFlag,
      history,
      albumFolder,
    } = this.props;

    if (
      allowsJoinTypeSelection(albumFolder) &&
      !albumFolder.force_join_without_purchase
    ) {
      setJoinTypeSelectionFlag(true);
    }

    onSelectIdentity(identity);

    history.push(`/discover/album-folder/${albumFolder.id}/select`);
  };

  onRemoveIdentity = identity => {
    const { onDeselectIdentity } = this.props;

    onDeselectIdentity(identity);
  };

  onAlbumCustomPrintShippingSelect = (includeShipping, albumCustomPrints) => {
    const { selectedIdentities, onSelectAlbumCustomPrint } = this.props;
    const albumCustomPrintIds = albumCustomPrints.map(({ id }) => id);

    // TODO: Improve this?
    selectedIdentities.forEach(selectedIdentity => {
      (selectedIdentity?.selectedAlbumCustomPrints ?? []).forEach(
        albumCustomPrint => {
          if (albumCustomPrintIds.includes(albumCustomPrint.id)) {
            onSelectAlbumCustomPrint(
              {
                ...albumCustomPrint,
                includeShipping,
              },
              selectedIdentity.id,
            );
          }
        },
      );
    });
  };

  onCheckout = (stripeToken = {}) => {
    const {
      history,
      albumFolder,
      selectedIdentities,
      user,
      checkoutAlbums,
      prepaidCheckoutItems,
      checkoutPrice,
    } = this.props;

    if (albumFolderRequiresPhotoSearchOnboarding(albumFolder)) {
      history.push(
        `/discover/album-folder/${albumFolder.id}/photo-search-checkout`,
      );
      return;
    }

    const {
      checkoutPpaItems,
      joinItems,
      customPrintItems,
    } = getAlbumFolderDiscoveryOnboardingItems(selectedIdentities);

    const purchaseItems = isPrepaidOnlyAlbumFolder(albumFolder)
      ? {
          ppaItems: checkoutPpaItems ?? [],
          photoItems: prepaidCheckoutItems?.photo_items ?? [],
          photoPrintItems: prepaidCheckoutItems?.photo_print_items ?? [],
          customPrintItems: prepaidCheckoutItems?.custom_print_items ?? [],
          digitalBundleItems: prepaidCheckoutItems?.digital_bundle_items ?? [],
        }
      : { ppaItems: checkoutPpaItems, customPrintItems };

    checkoutAlbums({
      accountId: user?.id,
      accountAddressId: user?.addresses?.[0]?.id,
      email: stripeToken.email,
      stripeToken: stripeToken.id,
      purchaseItems,
      joinItems,
      stripePaymentMethodId: stripeToken.stripe_payment_method_id,
      stripeConnectAccountId:
        stripeToken.stripe_connect_account_id ??
        checkoutPrice?.stripe_connect_account_id,
      promoCode: stripeToken.promo_code,
    });
  };

  toggleExpand = (identityId, entityId) => {
    const id = `${identityId}${entityId}`;

    this.setState(({ expanded }) => ({
      expanded: expanded.includes(id)
        ? expanded.filter(x => x !== id)
        : [...expanded, id],
    }));
  };

  isExpanded = (identityId, entityId) => {
    const { expanded } = this.state;
    const id = `${identityId}${entityId}`;
    return expanded.includes(id);
  };

  deselectAlbum = (album, identity) => {
    const { onDeselectAlbum } = this.props;
    onDeselectAlbum(album, identity);
  };

  deselectCustomPrint = (albumCustomPrint, identityId) => {
    const { onDeselectAlbumCustomPrint } = this.props;
    onDeselectAlbumCustomPrint(albumCustomPrint, identityId);
  };

  getShippingSelectionAlbumCustomPrints = () => {
    const { selectedIdentities } = this.props;

    return getAllSelectedAlbumCustomPrints(selectedIdentities).filter(
      canSelectAlbumCustomPrintShipping,
    );
  };

  hasItemsToBeShipped = () => {
    const { selectedIdentities, prepaidRequiresAddress } = this.props;
    const selectedAlbumCustomPrints = getAllSelectedAlbumCustomPrints(
      selectedIdentities,
    );

    return (
      prepaidRequiresAddress ||
      selectedAlbumCustomPrints.some(selectedAlbumCustomPrintWillShip)
    );
  };

  canCheckout = () => {
    const {
      loading,
      loadingCheckoutPrice,
      errorLoadingPrice,
      userAddress,
      loadingUserAddress,
      selectedIdentities,
      checkoutPrice,
    } = this.props;
    const hasItemsToBeShipped = this.hasItemsToBeShipped();

    if (hasItemsToBeShipped && !userAddress) {
      return false;
    }

    return (
      !loading &&
      !loadingCheckoutPrice &&
      !errorLoadingPrice &&
      !loadingUserAddress &&
      !!checkoutPrice &&
      selectedIdentities.length > 0
    );
  };

  getAccountReferralTypes = () => {
    const { selectedIdentities, prepaidCheckoutItems } = this.props;

    const referralTypes = [];
    const {
      checkoutPpaItems,
      customPrintItems,
    } = getAlbumFolderDiscoveryOnboardingItems(selectedIdentities);

    if (
      (prepaidCheckoutItems?.digital_bundle_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_download_items ?? []).length > 0 ||
      !!(prepaidCheckoutItems?.photo_items ?? []).find(
        i => !!i.digital_downloads_purchase,
      )
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.DIGITAL_DOWNLOADS);
    }

    if (
      (customPrintItems ?? []).length > 0 ||
      (prepaidCheckoutItems?.custom_print_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_items ?? []).length > 0 ||
      (prepaidCheckoutItems?.photo_print_items ?? []).length > 0
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRINTS);
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS);
    }

    if (
      (checkoutPpaItems ?? []).length > 0 ||
      (prepaidCheckoutItems?.subscription_items ?? []).length > 0
    ) {
      referralTypes.push(ACCOUNT_REFERRAL_TYPE.SUBSCRIPTIONS);
      if (!referralTypes.includes(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS)) {
        referralTypes.push(ACCOUNT_REFERRAL_TYPE.PRO_PRODUCTS);
      }
    }

    return referralTypes;
  };

  renderCharityDetails = () => {
    const { albumFolder } = this.props;
    const hasCharity = albumFolderHasCharity(albumFolder);
    const isPictureDay = !!albumFolder?.picture_day_enabled;
    const isPrepaid = albumFolderIsPrePaid(albumFolder);

    if (!hasCharity || isPrepaid || isPictureDay) {
      return null;
    }

    return (
      <Alert type="info" className="mt-2 mb-1 text-center">
        {(albumFolder && albumFolder.charity_description) ||
          '*50% goes to camper scholarships. Thank you!'}
      </Alert>
    );
  };

  renderPrePaidDetails = () => {
    const { albumFolder } = this.props;
    const isPrepaid = albumFolderIsPrePaid(albumFolder);

    if (!isPrepaid) {
      return null;
    }

    if (
      albumFolder?.album_subtype === ALBUM_SUBTYPE.EVENTS ||
      !albumFolder?.album_subtype
    ) {
      return null;
    }

    return (
      <Alert type="info" className="mt-2 mb-1">
        <p>
          <ContextualString
            id="prePaidAlbumsCheckoutMessage"
            type={albumFolder && albumFolder.album_type}
            subtype={albumFolder && albumFolder.album_subtype}
            defaultMessage="Great News, your family’s Waldo subscription has been pre-paid by {name}!"
            values={{
              name: getAccountCheckoutGreetingName(albumFolder.account),
            }}
          />
        </p>

        <p className="m-0">
          <ContextualString
            id="prePaidAlbumsCheckoutMessageSubtitle"
            type={albumFolder && albumFolder.album_type}
            subtype={albumFolder && albumFolder.album_subtype}
            defaultMessage="You can invite family and friends to view your photo stream via the app!"
            values={{
              name: getAccountCheckoutGreetingName(albumFolder.account),
            }}
          />
        </p>
      </Alert>
    );
  };

  checkForSiblingDiscount = () => {
    const { albumFolder, selectedIdentities, openModal } = this.props;
    if (
      ((albumFolder?.album_subtype === ALBUM_SUBTYPE.CAMPS &&
        albumFolder?.day_pricings_discount_percentage > 0) ||
        albumFolder?.siblings_discount_percentage > 0) &&
      selectedIdentities?.length === 1
    ) {
      openModal(SIBLING_DISCOUNT_ALERT_MODAL);
    }
  };

  render() {
    const {
      selectedIdentities,
      loading,
      error,
      albumFolder,
      prepaidCheckoutItems,
      checkoutPrice,
      loadingCheckoutPrice,
      errorLoadingPrice,
      userAddress,
      user,
      location,
      openModal,
      history,
      allowsPrepaidItems,
      prepaidPrintPackagePromoCode,
      setActiveIdentity,
      getIdentityCartItems,
    } = this.props;
    const {
      checkoutPpaItems,
      customPrintItems,
    } = getAlbumFolderDiscoveryOnboardingItems(selectedIdentities);

    const checkoutPriceVariables = isPrepaidOnlyAlbumFolder(albumFolder)
      ? { ...prepaidCheckoutItems, subscription_items: checkoutPpaItems }
      : {
          subscription_items: checkoutPpaItems,
          custom_print_items: customPrintItems,
        };

    const config = getConfig();
    const { baseUrl } = config?.app ?? {};
    const hasCharity = albumFolderHasCharity(albumFolder);
    const isPrepaid = albumFolderIsPrePaid(albumFolder);
    const isPictureDay = !!albumFolder?.picture_day_enabled;
    const shippingSelectableAlbumCustomPrints = this.getShippingSelectionAlbumCustomPrints();
    const hasItemsToBeShipped = this.hasItemsToBeShipped();
    const canCheckout = this.canCheckout();
    const identityNames = getSelectedIdentitiesNameString(selectedIdentities);
    const hasSearchSubscription =
      (user?.search_subscriptions?.records?.length ?? 0) > 0;

    const checkoutBtnClasses = classNames({
      'btn-join w-100': true,
      'mt-1': !prepaidPrintPackagePromoCode,
      'mt-2': prepaidPrintPackagePromoCode,
    });

    return (
      <Fragment>
        <WaldoSpeechBubble className="w-100-i mb-4" position="right">
          {allowsPrepaidItems ? (
            <Fragment>
              Add additional{' '}
              {
                <ContextualString
                  id="identityReference"
                  defaultMessage="person"
                  type={albumFolder?.album_type}
                  subtype={albumFolder?.album_subtype}
                  formatter={contextualString => pluralize(contextualString, 2)}
                />
              }{' '}
              or select your purchase option below!
            </Fragment>
          ) : (
            <Fragment>
              You will receive matched photos in {albumFolder?.name} for{' '}
              {identityNames}!
            </Fragment>
          )}
        </WaldoSpeechBubble>

        {shouldDisplayBrandAttribution(albumFolder) && (
          <PhotographerAttributionBanner account={albumFolder.account} />
        )}

        <div className="discovery-album-folder-summary-content">
          <div className="discovery-album-folder-summary-scroll-wrapper">
            <div className="discovery-album-folder-summary-scroll">
              <div className="container">
                <div className="row">
                  <div className="col-12 px-0">
                    {this.renderCharityDetails()}
                    {this.renderPrePaidDetails()}
                  </div>
                </div>
              </div>

              <div className="flex-list">
                {error && <Alert type="danger">{error}</Alert>}

                {errorLoadingPrice && (
                  <Alert type="danger">
                    Something went wrong and we can&rsquo;t process your request
                    right now. Please try again later, our top people are
                    looking into the issue!
                  </Alert>
                )}

                {selectedIdentities.map(identity =>
                  identity.selectedAlbums.length > 0 ? (
                    <DiscoverAlbumFolderSummaryIdentity
                      identity={identity}
                      isExpanded={this.isExpanded}
                      toggleExpand={this.toggleExpand}
                      deselectAlbum={this.deselectAlbum}
                      deselectCustomPrint={this.deselectCustomPrint}
                      onEditSelections={this.onEditIdentitySelections}
                      onRemoveIdentity={this.onRemoveIdentity}
                      hasSearchSubscription={
                        hasSearchSubscription &&
                        albumFolderRequiresPhotoSearchOnboarding(albumFolder)
                      }
                      identityCartItems={
                        isPrepaidOnlyAlbumFolder(albumFolder)
                          ? getIdentityCartItems({ identityId: identity.id })
                          : []
                      }
                      onEditPrepaidItems={() => {
                        setActiveIdentity(identity);
                        history.replace({
                          pathname: `/purchase/album-folder/${albumFolder.id}/pre-purchase`,
                          state: {
                            fromDiscovery: true,
                          },
                        });
                      }}
                      key={identity.id}
                    />
                  ) : null,
                )}

                {shippingSelectableAlbumCustomPrints.length > 0 && (
                  <AlbumCustomPrintShippingSelection
                    albumCustomPrints={shippingSelectableAlbumCustomPrints}
                    onShippingSelection={this.onAlbumCustomPrintShippingSelect}
                    className="mt-2"
                  />
                )}

                {hasItemsToBeShipped && (
                  <DiscoverAlbumFolderUserAddressContainer
                    address={userAddress}
                    userId={user?.id}
                    className="my-2"
                    addressDisplayTitle="Shipped to:"
                  />
                )}
              </div>
            </div>
          </div>
          {albumFolder?.album_identity_access !==
            ALBUM_IDENTITY_ACCESS.PRIMARY_ONLY && (
            <Button
              btnStyle="link"
              btnBlock
              className="text-extra-bold mt-2 d-flex align-items-center"
              onClick={() =>
                history.push(`/discover/album-folder/${albumFolder.id}/faces`)
              }
            >
              <PlusIcon className="mr-1" color="#0d84fe" size={14} />
              Add more{' '}
              <ContextualString
                id="identityReference"
                type={albumFolder?.album_type}
                subtype={albumFolder?.album_subtype}
                formatter={str => pluralize(str, 2)}
              />
            </Button>
          )}
        </div>

        <OnboardingLayoutFooter fixed>
          <div className="flex-list w-100">
            {checkoutPpaItems.length > 0 && (
              <CheckoutPrice
                loading={loadingCheckoutPrice}
                totalPrice={getTotalCheckoutPrice(selectedIdentities)}
                finalPrice={checkoutPrice?.total_price?.value}
                shippingPrice={checkoutPrice?.total_shipping_cost?.value}
                taxPrice={checkoutPrice?.total_tax?.value}
                className="mt-2"
                showTotalPrice={false}
              />
            )}
          </div>

          {hasCharity && !isPrepaid && !isPictureDay && (
            <p className="text-xs text-muted text-center charity-footnote">
              {albumFolder?.charity_footnote ? (
                albumFolder.charity_footnote
              ) : (
                <span>
                  *Up to $22.50 per{' '}
                  <ContextualString
                    id="identityReference"
                    defaultMessage="person"
                    type={albumFolder?.album_type}
                    subtype={albumFolder?.album_subtype}
                  />
                  .
                </span>
              )}
            </p>
          )}

          <div className="discover-album-folder-checkout-btn-wrapper">
            {checkoutPrice?.total_price?.value > 0 ? (
              <Button
                btnBlock
                btnSize="lg"
                className="btn-checkout mt-1"
                disabled={!canCheckout}
                onClick={() => {
                  if (!canCheckout) {
                    return;
                  }

                  openModal(STRIPE_CHECKOUT_MODAL, {
                    price: checkoutPrice?.total_price,
                    buttonLabel: `Pay $${formatPriceForDisplay(
                      checkoutPrice?.total_price?.value,
                    )}`,
                    onSuccess: async token => {
                      await this.onCheckout(token);
                    },
                    cardRequired: false,
                    defaultName: user?.name ?? '',
                    defaultEmail: user?.email_address ?? '',
                    accountReferralTypes: this.getAccountReferralTypes(),
                    checkoutPriceVariables: ({ promo_code: promoCode }) => {
                      return {
                        account_id: user?.id,
                        address_id: userAddress?.id,
                        promo_code: promoCode,
                        ...checkoutPriceVariables,
                      };
                    },
                  });

                  gtm.trackPageView({
                    url: `${baseUrl}${location.pathname}/virtual/payment`,
                    title: 'Virtual Pageview - Payment',
                    userId: user.id,
                  });
                }}
              >
                {loading || loadingCheckoutPrice ? (
                  'Loading...'
                ) : (
                  <span>
                    Waldo, find my photos! (
                    <PriceDisplay
                      showDiscount
                      trimDecimalZeros={false}
                      price={checkoutPrice?.total_price?.value}
                      oldPrice={
                        checkoutPrice?.total_price?.value +
                        (checkoutPrice?.total_discount_price?.value ?? 0)
                      }
                    />
                    )
                  </span>
                )}
              </Button>
            ) : (
              <Fragment>
                <Button
                  btnBlock
                  btnSize="lg"
                  disabled={!canCheckout}
                  onClick={this.onCheckout}
                  className={checkoutBtnClasses}
                >
                  {loading || loadingCheckoutPrice ? (
                    'Loading...'
                  ) : (
                    <Fragment>
                      {allowsPrepaidItems ? (
                        <span>
                          View Purchase Options
                          {prepaidPrintPackagePromoCode && (
                            <Fragment>
                              {' '}
                              &amp; Save{' '}
                              {prepaidPrintPackagePromoCode.discount.mode ===
                              DISCOUNT_MODE.PERCENTAGE
                                ? `${prepaidPrintPackagePromoCode.discount.value}%`
                                : `$${formatPriceForDisplay(
                                    prepaidPrintPackagePromoCode.discount.value,
                                    true,
                                  )}`}
                              !
                            </Fragment>
                          )}
                        </span>
                      ) : (
                        <div className="d-flex flex-column pos-rel">
                          {isPictureDay
                            ? 'Preview for free!'
                            : 'Waldo, find my photos!'}
                          {albumFolderRequiresPhotoSearchOnboarding(
                            albumFolder,
                          ) &&
                            isEligibleForShutterbugFreeTrial(user) && (
                              <Badge
                                badgeStyle="yellow"
                                className="album-purchase-free-trial-badge"
                              >
                                {SHUTTERBUG_TRIAL_DAYS_FALLBACK}-DAY FREE TRIAL
                              </Badge>
                            )}
                        </div>
                      )}
                    </Fragment>
                  )}
                </Button>
                {allowsPrepaidItems && (
                  <Button
                    className="mt-1"
                    btnSize="sm"
                    btnStyle="link"
                    disabled={!canCheckout}
                    onClick={e => {
                      this.setState(() => ({
                        skipPrintPackages: true,
                      }));
                      this.onCheckout(e);
                    }}
                  >
                    No thanks, purchase later
                  </Button>
                )}
              </Fragment>
            )}
          </div>
        </OnboardingLayoutFooter>

        <SiblingDiscountAlertModal
          onRequestClose={() => {
            closeModal(SIBLING_DISCOUNT_ALERT_MODAL);
          }}
          onAddIdentity={() => {
            history.replace(`/discover/album-folder/${albumFolder.id}/faces`);
          }}
          albumSubtype={albumFolder?.album_subtype}
        />
      </Fragment>
    );
  }
}

export default DiscoverAlbumFolderSummary;
